/**
 * Media source (Google Drive) events from the websocket, for the components waiting on them.
 *
 * The picker token and import results are per-request and short-lived, so they are handed straight
 * to whoever subscribed rather than kept in the redux store.
 */

export enum MediaSourceEventType {
    PICKER_TOKEN = "mediaSourcePickerToken",
    PICKER_TOKEN_FAILED = "mediaSourcePickerTokenFailed",
    FILE_IMPORTED = "mediaSourceFileImported",
    FILE_IMPORT_FAILED = "mediaSourceFileImportFailed",
}

export interface MediaSourceType {
    uuid: string;
    name: string;
    /** The Google account's email. */
    description?: string | null;
    handler: string;
}

export interface MediaSourceImportType {
    uuid: string;
    connector: string;
    peerActivity?: string | null;
    fileName: string;
    status: "pending" | "imported" | "failed" | "cancelled";
    errorCode?: string | null;
    errorMessage?: string | null;
    /** The post media item, once imported. */
    media?: any;
}

export interface MediaSourcePickerTokenType {
    requestId: string;
    connector?: string;
    accessToken?: string | null;
    expiresAt?: string | null;
    errorCode?: string | null;
}

type Listener = (eventType: MediaSourceEventType, items: any[]) => void;

const listeners = new Set<Listener>();

export const isMediaSourceEvent = (eventType: string): eventType is MediaSourceEventType =>
    (Object.values(MediaSourceEventType) as string[]).includes(eventType);

export const emitMediaSourceEvent = (eventType: MediaSourceEventType, items: any[]) => {
    listeners.forEach(listener => listener(eventType, items));
};

/** @returns unsubscribe */
export const onMediaSourceEvent = (listener: Listener): (() => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

/**
 * Resolves with the picker token for `requestId`, or rejects with the error code (or 'timeout').
 */
export const waitForPickerToken = (requestId: string, timeoutMs = 20000): Promise<MediaSourcePickerTokenType> => {
    return new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
            unsubscribe();
            reject(new Error('timeout'));
        }, timeoutMs);

        const unsubscribe = onMediaSourceEvent((eventType, items) => {
            if (eventType !== MediaSourceEventType.PICKER_TOKEN && eventType !== MediaSourceEventType.PICKER_TOKEN_FAILED) {
                return;
            }
            const item: MediaSourcePickerTokenType | undefined = (items || []).find((i: MediaSourcePickerTokenType) => i?.requestId === requestId);
            if (item === undefined) {
                return;
            }

            clearTimeout(timer);
            unsubscribe();

            if (eventType === MediaSourceEventType.PICKER_TOKEN && item.accessToken) {
                resolve(item);
            } else {
                reject(new Error(item.errorCode || 'unknown'));
            }
        });
    });
};
